
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { User, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const formSchema = z.object({
  email: z.string().email("E-mail inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
});

type FormValues = z.infer<typeof formSchema>;

const Login = () => {
  const { login, isLoading } = useAuth();
  const [showResetPassword, setShowResetPassword] = useState(false);
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    await login(values.email, values.password);
  };

  const resetPasswordForm = useForm({
    resolver: zodResolver(z.object({
      email: z.string().email("E-mail inválido"),
    })),
    defaultValues: {
      email: "",
    },
  });

  const handleResetPassword = async (values: { email: string }) => {
    toast.success("Se o e-mail existir, você receberá instruções para recuperar sua senha");
    setShowResetPassword(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-hero-blue to-hero-green p-4">
      <div className="absolute top-4 left-4">
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-white hover:bg-white/10"
          onClick={() => navigate("/")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>
      
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src="/lovable-uploads/39686db0-4967-4556-a4f9-65cbf5776672.png" 
            alt="Logo" 
            className="h-24 w-auto drop-shadow-lg" 
          />
        </div>
        
        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm rounded-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-hero-blue">Acesso ao Sistema</CardTitle>
            <CardDescription className="text-gray-600">
              Entre com suas credenciais
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {!showResetPassword ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">E-mail</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <User className="h-4 w-4" />
                            </span>
                            <Input 
                              placeholder="seu@email.com" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue" 
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Senha</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <Lock className="h-4 w-4" />
                            </span>
                            <Input 
                              type="password" 
                              placeholder="••••••" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end">
                    <Button 
                      type="button" 
                      variant="link" 
                      className="text-sm text-hero-blue p-0 hover:text-hero-blue-dark"
                      onClick={() => setShowResetPassword(true)}
                    >
                      Esqueceu sua senha?
                    </Button>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-hero-blue hover:bg-hero-blue-dark text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? "Entrando..." : "Entrar"}
                  </Button>
                </form>
              </Form>
            ) : (
              <Form {...resetPasswordForm}>
                <form onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)} className="space-y-4">
                  <FormField
                    control={resetPasswordForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">E-mail</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <User className="h-4 w-4" />
                            </span>
                            <Input 
                              placeholder="seu@email.com" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowResetPassword(false)}
                    >
                      Voltar
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-hero-blue hover:bg-hero-blue-dark text-white"
                    >
                      Recuperar Senha
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </CardContent>
          <CardFooter className="text-center pt-0">
            <p className="text-sm text-gray-600 w-full">
              Ainda não tem uma conta?{" "}
              <Link to="/cadastro" className="text-hero-blue hover:underline hover:text-hero-blue-dark font-medium">
                Cadastre-se
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Login;
